import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Smartphone, Home, User } from 'lucide-react';
import { useDeviceRegistry } from '../../hooks/useDeviceRegistry';
import Switch from '../form/switch/Switch';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onConfigured?: () => void;
};

export default function DeviceConfigModal({ isOpen, onClose, onConfigured }: Props) {
  const { t } = useTranslation();
  const { 
    deviceId, 
    isHomeDevice, 
    isPrimaryDevice,
    familyHomeDeviceId,
    setHomeDeviceEnabled,
    setPrimaryDeviceConfirmed,
    platform 
  } = useDeviceRegistry();
  
  const [isUpdating, setIsUpdating] = useState(false);

  if (!isOpen) return null;

  const handleSetAsHome = async () => {
    setIsUpdating(true);
    try {
      await setHomeDeviceEnabled(!isHomeDevice);
      if (onConfigured) onConfigured();
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSetAsPrimary = async () => {
    setIsUpdating(true);
    try {
      await setPrimaryDeviceConfirmed(!isPrimaryDevice);
      if (onConfigured) onConfigured();
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div 
        className="w-full max-w-md rounded-[24px] shadow-sm overflow-hidden"
        style={{ backgroundColor: '#fff5eb' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-5 rounded-t-[24px]"
          style={{ backgroundColor: '#055590' }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Smartphone className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-black text-white">
              {t('calls.deviceConfig.title', { defaultValue: 'Configuración de Dispositivo' })}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-2xl hover:bg-white/20 flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          {/* Device Info */}
          <div className="p-4 rounded-2xl shadow-sm"
            style={{ backgroundColor: '#ffffff', opacity: 0.8 }}
          >
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">
                  {t('calls.deviceConfig.platform', { defaultValue: 'Plataforma' })}:
                </span>
                <span className="font-black text-gray-900 dark:text-white capitalize">{platform}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">
                  {t('calls.deviceConfig.deviceId', { defaultValue: 'ID' })}:
                </span>
                <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                  {deviceId || '—'}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">
                  {t('calls.deviceConfig.familyHomeDeviceId', { defaultValue: 'ID Casa (familia)' })}:
                </span>
                <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                  {familyHomeDeviceId || '—'}
                </span>
              </div>
            </div>
          </div>

          {/* Home Device Toggle */}
          <div className="p-4 rounded-2xl shadow-sm"
            style={{ backgroundColor: '#ffffff', opacity: 0.8 }}
          >
            <div className="flex items-start gap-3">
              <Home className="w-5 h-5 text-gray-600 dark:text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <Switch
                  label={t('calls.deviceConfig.isHomeDevice', { defaultValue: 'Dispositivo de Casa' })}
                  checked={isHomeDevice}
                  disabled={isUpdating}
                  onChange={() => {
                    void handleSetAsHome();
                  }}
                />
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {t('calls.homeDevice.description', { defaultValue: 'Marca este dispositivo como el teléfono de casa para recibir llamadas familiares' })}
                </p>
              </div>
            </div>
          </div>

          {/* Primary Device Toggle */}
          <div className="p-4 rounded-2xl shadow-sm"
            style={{ backgroundColor: '#ffffff', opacity: 0.8 }}
          >
            <div className="flex items-start gap-3">
              <User className="w-5 h-5 text-gray-600 dark:text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <Switch
                  label={t('calls.deviceConfig.isPrimaryDevice', { defaultValue: 'Dispositivo Principal' })}
                  checked={isPrimaryDevice}
                  disabled={isUpdating}
                  onChange={() => {
                    void handleSetAsPrimary();
                  }}
                  color="gray"
                />
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {t('calls.primaryDevice.description', { defaultValue: 'Esto nos ayuda a dirigir las llamadas a tu dispositivo principal.' })}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-5">
          <button
            onClick={onClose}
            className="w-full py-3 rounded-2xl font-black shadow-sm transition-colors"
            style={{ backgroundColor: '#ef6e6b', color: '#ffffff' }}
          >
            {t('common.close', { defaultValue: 'Cerrar' })}
          </button>
        </div>
      </div>
    </div>
  );
}
